<app-modal
  title="Configure Category Within Taxonomy"
  subtitle="Define contextual rules and operational status"
  (closed)="closed.emit()"
>
  <!-- Category (read-only) + display order (JCCA-175) -->
  <div class="grid grid-cols-[1fr_7rem] gap-3 mb-5">
    <div class="flex flex-col gap-1.5 min-w-0">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Category</label>
      <div
        class="w-full px-3 py-2.5 rounded-xl border border-surface-300 bg-slate-50 text-xs text-navy-900 truncate"
      >
        {{ categoryName }}
      </div>
    </div>
    <div class="flex flex-col gap-1.5">
      <label
        class="text-xs font-semibold text-navy-600 tracking-wider"
        title="Lower numbers are listed first. Leave empty to sort by name, after the numbered ones."
        >Display order</label
      >
      <input
        type="number"
        step="any"
        placeholder="—"
        [value]="displayOrder()"
        (input)="displayOrder.set($any($event.target).value)"
        [class.border-red-400]="displayOrderInvalid()"
        class="w-full rounded-xl border border-surface-300 px-3 py-2.5 text-xs text-navy-900 placeholder:text-slate-400 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 focus:outline-none transition"
      />
    </div>
  </div>
  <p class="text-2xs text-slate-400 -mt-3 mb-5">
    Categories are listed by display order (lower first), then by name.
  </p>

  @if (loading()) {
    <div class="flex items-center justify-center py-8">
      <span class="text-xs text-slate-400">Loading...</span>
    </div>
  }

  <!-- Description -->
  <div class="flex flex-col gap-1.5 mb-5">
    <label class="text-xs font-semibold text-navy-600 tracking-wider">Description</label>
    <textarea
      placeholder="Define category description..."
      rows="4"
      [value]="definition()"
      (input)="definition.set($any($event.target).value)"
      class="w-full resize-none rounded-xl border border-surface-300 px-3 py-2.5 text-xs text-navy-900 placeholder:text-slate-400 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 focus:outline-none transition"
    ></textarea>
  </div>

  <!-- Status -->
  <div class="flex flex-col gap-1.5 mb-5">
    <label class="text-xs font-semibold text-navy-600 tracking-wider">Status</label>
    <div class="flex items-center bg-slate-100 rounded-3xl px-1 py-1 gap-1">
      <button
        type="button"
        (click)="active.set(true)"
        class="flex-1 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer"
        [ngClass]="
          active() ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500 hover:text-navy-700'
        "
      >
        Active
      </button>
      <button
        type="button"
        (click)="active.set(false)"
        class="flex-1 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer"
        [ngClass]="
          !active() ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500 hover:text-navy-700'
        "
      >
        Inactive
      </button>
    </div>
  </div>

  <!-- Legal & Oversight Recipients — situation taxonomies only -->
  @if (type() === 'situation') {
    <div class="flex flex-col gap-2 mb-5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider"
        >Legal &amp; Oversight Recipients</label
      >

      @for (recipient of selectedRecipients(); track recipient.id) {
        <div
          class="flex items-center justify-between px-3 py-2.5 rounded-xl border border-surface-300 bg-white text-xs text-navy-900"
        >
          <span>{{ recipient.name }}</span>
          <button
            type="button"
            (click)="removeRecipient(recipient.id)"
            class="text-slate-400 hover:text-red-500 transition-colors cursor-pointer ml-2 shrink-0"
          >
            <img src="/icons/x-icon.svg" class="w-3.5 h-3.5" alt="Remove" />
          </button>
        </div>
      }

      @if (showRecipientSelect()) {
        <app-select
          placeholder="Select recipient..."
          [options]="availableRecipients()"
          value=""
          (changed)="addRecipient($event)"
        />
      } @else {
        <button
          type="button"
          (click)="showRecipientSelect.set(true)"
          class="flex items-center gap-1 text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors cursor-pointer py-1"
        >
          <span class="text-base leading-none">+</span> Add Recipients
        </button>
      }
    </div>
  }

  <!-- Data elements shown on incidents with this category (JCCA-159), situation taxonomies only -->
  @if (type() === 'situation') {
    <div class="flex flex-col gap-2 mb-5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Data Elements</label>
      <p class="text-2xs text-slate-400 -mt-1">
        Shown in Additional Data when an incident has this category, in addition to the program's
        data elements.
      </p>

      @for (link of linkedDataElements(); track link.configuredDataElementId) {
        <div
          class="flex items-center justify-between px-3 py-2.5 rounded-xl border border-surface-300 bg-white text-xs text-navy-900"
        >
          <span class="min-w-0 truncate">{{ dataElementName(link) }}</span>
          <label class="flex items-center gap-1.5 cursor-pointer select-none ml-auto shrink-0">
            <input
              type="checkbox"
              [checked]="link.requiredOptional === 1"
              (change)="
                setDataElementRequired(link.configuredDataElementId, $any($event.target).checked)
              "
              class="w-3.5 h-3.5 rounded cursor-pointer accent-accent-500"
            />
            <span class="text-xs text-slate-500">Required</span>
          </label>
          <button
            type="button"
            (click)="removeDataElement(link.configuredDataElementId)"
            title="Remove"
            aria-label="Remove data element"
            class="text-slate-400 hover:text-red-500 transition-colors cursor-pointer ml-2 shrink-0"
          >
            <img src="/icons/x-icon.svg" class="w-3.5 h-3.5" alt="" />
          </button>
        </div>
      }

      @if (showDataElementSelect()) {
        <app-select
          placeholder="Select data element..."
          [options]="availableDataElements()"
          [searchable]="true"
          value=""
          (changed)="addDataElement($event)"
        />
      } @else {
        <button
          type="button"
          (click)="showDataElementSelect.set(true)"
          class="flex items-center gap-1 text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors cursor-pointer py-1"
        >
          <span class="text-base leading-none">+</span> Add Data Element
        </button>
      }
    </div>
  }

  <div modal-footer class="flex items-center justify-between gap-3">
    <button
      type="button"
      (click)="onRemove()"
      [disabled]="removing()"
      class="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <img src="/icons/trash-icon.svg" class="w-4 h-4" alt="" />
      {{ removing() ? 'Removing...' : 'Remove Category from Taxonomy' }}
    </button>

    <div class="flex items-center gap-3">
      <button
        type="button"
        (click)="closed.emit()"
        class="px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="onSubmit()"
        [disabled]="submitting() || !hasChanges() || displayOrderInvalid()"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ submitting() ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
  </div>
</app-modal>
